import type { NavigationConfig, NavItemConfig, IconKey, UserRole } from '@/lib/navigation';

/**
 * Navigation Configuration
 *
 * This is the SINGLE SOURCE OF TRUTH for all navigation structure.
 *
 * Rules:
 * - roles: [] means visible to all authenticated users
 * - roles: ['DataQualityAdmin'] means visible to DataQualityAdmin and SiteAdmin
 * - roles: ['SiteAdmin'] means visible only to SiteAdmin
 * - SiteAdmin automatically sees everything (handled in useNavigation)
 * - Sections and items are sorted by their 'order' property
 */

function adminItem(
  id: string, label: string, href: string, icon: IconKey, order: number, roles: UserRole[] = ['DataQualityAdmin'],
): NavItemConfig {
  return { id, label, href, icon, roles, order };
}

function tableItems(entries: [id: string, label: string, href: string][]): NavItemConfig[] {
  return entries.map(([id, label, href], index) => adminItem(id, label, href, 'table', index));
}

export const navigationConfig: NavigationConfig = {
  sections: [
    {
      id: 'general',
      label: 'General',
      order: 0,
      roles: [],
      items: [
        { id: 'home', label: 'Home', href: '/', icon: 'home', roles: [], order: 1 },
        // PRODOPSAI-694: both surfaces are named by scope, not audience. The
        // ids/hrefs stay as-is so existing deep links and bookmarks resolve.
        { id: 'metric-view', label: 'Team & Product View', href: '/metric-view', icon: 'chart-bar', roles: [], order: 2 },
        { id: 'management-view', label: 'Organisational View', href: '/management-view', icon: 'chart-bar', roles: [], order: 2.5 },
        { id: 'ask', label: 'Ask AI', href: '/ask', icon: 'chat-bubble', roles: [], order: 3 },
        { id: 'people', label: 'People', href: '/people', icon: 'users', roles: [], order: 3.5 },
        { id: 'team-visualization', label: 'Teams', href: '/teams', icon: 'users', roles: [], order: 3.6 },
        { id: 'metric-catalogue', label: 'Definitions Catalogue', href: '/metric-catalogue', icon: 'book-open', roles: [], order: 4 },
      ],
    },
    {
      id: 'data-quality-admin',
      label: 'Data/Quality Admin',
      order: 50,
      roles: ['DataQualityAdmin'],
      divider: true,
      items: [
        adminItem('quality-control', 'Quality Control', '/admin/quality-control', 'shield-check', -1),
        adminItem('homepage-content', 'Homepage Content', '/admin/homepage-content', 'layout-dashboard', -0.5),
        adminItem('org-hierarchy', 'Org Hierarchy', '/admin/org-hierarchy', 'sitemap', 0),
        adminItem('map-jira-status', 'Map Jira Status', '/admin/map-jira-status', 'map-pin', 1),
        adminItem('teams-management', 'Manage Teams', '/admin/teams', 'users', 2),
        adminItem('team-mapping', 'Team mapping', '/admin/team-mapping', 'users', 2.4),
        adminItem('people', 'People', '/admin/people', 'users', 2.5),
        adminItem('scheduled-jobs', 'Scheduled Jobs', '/admin/scheduled-jobs', 'clock', 3),
        adminItem('pipeline-status', 'Pipeline Status', '/admin/pipeline-status', 'activity', 4),
        adminItem('metric-definitions', 'Metric Definitions', '/admin/metric-definitions', 'cog', 5),
        adminItem('metric-groups', 'Metric Groups', '/admin/metric-groups', 'layout-dashboard', 6),
      ],
    },
    {
      id: 'raw-data-utilities',
      label: 'Raw Data — Utilities',
      order: 75,
      roles: ['DataQualityAdmin'],
      divider: true,
      items: [
        adminItem('fabric-table-browser', 'Table Browser', '/admin/table-browser', 'database', 0),
        adminItem('repository-connections', 'Repository Connections', '/admin/repository-connections', 'link', 1),
        adminItem('devex-team-mapping', 'DevEx Team Mapping', '/admin/devex-team-mapping', 'users', 2),
        adminItem('bim-relationships', 'BIM Relationships', '/admin/bim-relationships', 'link', 3),
      ],
    },
    {
      id: 'raw-data-dimensions',
      label: 'Raw Data — Dimensions',
      order: 80,
      roles: ['DataQualityAdmin'],
      items: tableItems([
        ['tbl-bridge-repository-product', 'bridge_repository_product', '/admin/tbl-bridge-repository-product'],
        ['tbl-dim-issue', 'dim_issue', '/admin/tbl-dim-issue'],
        ['tbl-dim-product', 'dim_product', '/admin/tbl-dim-product'],
        ['tbl-dim-repository', 'dim_repository', '/admin/tbl-dim-repository'],
        ['tbl-dim-sonarqube-project', 'dim_sonarqube_project', '/admin/tbl-dim-sonarqube-project'],
        ['tbl-dim-team', 'dim_team', '/admin/tbl-dim-team'],
      ]),
    },
    {
      id: 'raw-data-jira',
      label: 'Raw Data — Jira',
      order: 82,
      roles: ['DataQualityAdmin'],
      items: tableItems([
        ['tbl-jira-issues', 'jira_issues', '/admin/tbl-jira-issues'],
        ['tbl-jira-status-changes', 'jira_status_changes', '/admin/tbl-jira-status-changes'],
      ]),
    },
    {
      id: 'raw-data-facts',
      label: 'Raw Data — Facts',
      order: 85,
      roles: ['DataQualityAdmin'],
      items: tableItems([
        ['tbl-deployments', 'fact_deployment', '/admin/tbl-deployments'],
        ['tbl-incidents', 'fact_incident', '/admin/tbl-incidents'],
        ['tbl-issue-current', 'fact_issue_current', '/admin/tbl-issue-current'],
        ['tbl-issue-in-progress-daily', 'fact_issue_in_progress_daily', '/admin/tbl-issue-in-progress-daily'],

        ['tbl-pull-requests', 'fact_pull_request', '/admin/tbl-pull-requests'],
        ['tbl-fact-sonarqube-project-quality-history', 'SonarQube Quality History', '/admin/tbl-fact-sonarqube-project-quality-history'],
        ['tbl-fact-sonarqube-project-quality-latest', 'SonarQube Quality Latest', '/admin/tbl-fact-sonarqube-project-quality-latest'],
      ]),
    },
    {
      id: 'raw-data-views',
      label: 'Raw Data — Views & Sync',
      order: 90,
      roles: ['DataQualityAdmin'],
      items: tableItems([
        ['tbl-sync-status-curated', 'Sync Status (curated)', '/admin/tbl-sync-status-curated'],
        ['tbl-sync-status-raw', 'Sync Status (raw)', '/admin/tbl-sync-status-raw'],
        ['tbl-devex-quarterly-normalized', 'DevEx Quarterly (normalized)', '/admin/tbl-devex-quarterly-normalized'],
        ['tbl-devex-quarterly-raw', 'DevEx Quarterly (raw)', '/admin/tbl-devex-quarterly-raw'],
      ]),
    },
    {
      id: 'site-admin',
      label: 'Site Admin',
      order: 100,
      roles: ['SiteAdmin'],
      divider: true,
      items: [
        adminItem('user-management', 'Users', '/admin/users', 'users', 0, ['SiteAdmin']),
        adminItem('ai-cost', 'AI Cost', '/admin/ai-cost', 'chart-bar', 2.7, ['SiteAdmin']),
        adminItem('login-activity', 'Login Activity', '/admin/login-activity', 'clock', 3, ['SiteAdmin']),
        adminItem('scripts', 'Scripts', '/admin/scripts', 'command-line', 4, ['SiteAdmin']),
        adminItem('logs', 'Logs', '/admin/logs', 'document-text', 5, ['SiteAdmin']),
        adminItem('settings', 'Admin Settings', '/admin/settings', 'cog', 6, ['SiteAdmin']),
      ],
    },
  ],
};
